import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Alert, Grid } from '@mui/material';
import { CoverageCodePicker } from './CoverageCodePicker';
import {
    ClientFilingDefaults, StateFilingDefault, SPECIAL_LICENSE_STATES,
    getFilingEnabledStates, getClientFilingDefaults, saveClientFilingDefaults,
} from '../../helper/clientFilingDefaults';

/**
 * Per-client filing defaults editor (Batch Filing Workflow checklist #8). Shared by the standalone
 * admin page (Pages/ClientFilingDefaults) and the per-client pop-out on the Batch Review screen, so
 * both surfaces edit the exact same codes. MISC picks a client, then sets the default carrier + a
 * coverage code per state (+ an agency license for the four special-license states). Every field shows
 * the system default (extraction / License Matrix) with an override and a revert-to-default.
 */
const BLUE = '#2099d2';
const BLUE_HOVER = '#176990';
const SLATE_500 = '#475569';
const SLATE_400 = '#64748b';
const BORDER = '#e2e8f0';

const REVERT_SX = {
    background: 'none', border: 'none', p: 0, cursor: 'pointer',
    fontSize: '11.5px', fontWeight: 700, color: BLUE, alignSelf: 'flex-start',
    '&:hover': { textDecoration: 'underline' }, '&:disabled': { color: '#c3ccd4', cursor: 'default', textDecoration: 'none' },
} as const;

interface Props {
    token?: string;
    partnersId: number;
    partnerName?: string;
    /** Compact spacing for the pop-out dialog. */
    dense?: boolean;
    /** Coverage-only mode (the Partners/Client page override section, Jerry 2026-07-16): show ONLY the
     *  per-state coverage-code override — hide the client-wide carrier block and the special-license
     *  column. Any existing carrier/license values still round-trip on save (they're just not edited here),
     *  so this and the full standalone editor stay the same source of truth. */
    coverageOnly?: boolean;
}

export function ClientFilingDefaultsPanel({ token, partnersId, partnerName, dense, coverageOnly }: Props) {
    const [loading, setLoading] = useState(true);
    const [backendUnavailable, setBackendUnavailable] = useState(false);
    const [carrierName, setCarrierName] = useState('');
    const [carrierNaic, setCarrierNaic] = useState('');
    const [carrierNaicDefault, setCarrierNaicDefault] = useState<string | null>(null);
    const [rows, setRows] = useState<StateFilingDefault[]>([]);
    const [baseline, setBaseline] = useState('');
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [saveMessage, setSaveMessage] = useState<string | null>(null);

    // Snapshot the editable values so Save enables only on a real change (and re-disables after a save).
    const snapshot = useCallback((name: string, naic: string, list: StateFilingDefault[]) =>
        JSON.stringify({ name, naic, s: list.map((r) => [r.stateId, r.coverageOverride, r.licenseOverride]) }), []);

    useEffect(() => {
        if (!token || !partnersId) return;
        let cancelled = false;
        setLoading(true);
        setSaveStatus('idle');
        setSaveMessage(null);
        (async () => {
            // The per-state rows always come from the live filing-capability registry; the client's saved
            // overrides/defaults are merged on top when the backend is available.
            const [states, defaultsRes] = await Promise.all([
                getFilingEnabledStates(token),
                getClientFilingDefaults(token, partnersId),
            ]);
            if (cancelled) return;
            const saved: ClientFilingDefaults | undefined = defaultsRes.data;
            const savedByState = new Map((saved?.states || []).map((s) => [s.stateId, s]));
            const merged: StateFilingDefault[] = states.map((st) => {
                const s = savedByState.get(st.stateId);
                return {
                    stateId: st.stateId,
                    stateName: st.name,
                    postalAbbv: st.postalAbbv,
                    coverageOverride: s?.coverageOverride ?? null,
                    coverageDefault: s?.coverageDefault ?? null,
                    coverageDefaultName: s?.coverageDefaultName ?? null,
                    licenseOverride: s?.licenseOverride ?? null,
                    licenseDefault: s?.licenseDefault ?? null,
                    special: SPECIAL_LICENSE_STATES.includes(st.postalAbbv),
                };
            });
            const name = saved?.carrierName ?? '';
            const naic = saved?.carrierNaic ?? '';
            setCarrierName(name);
            setCarrierNaic(naic);
            setCarrierNaicDefault(saved?.carrierNaicDefault ?? null);
            setRows(merged);
            setBaseline(snapshot(name, naic, merged));
            setBackendUnavailable(defaultsRes.unavailable);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [token, partnersId, snapshot]);

    const setRowField = (stateId: number, key: 'coverageOverride' | 'licenseOverride', value: string | null) => {
        setRows((prev) => prev.map((r) => (r.stateId === stateId ? { ...r, [key]: value } : r)));
    };

    const hasChanges = useMemo(
        () => snapshot(carrierName, carrierNaic, rows) !== baseline,
        [carrierName, carrierNaic, rows, baseline, snapshot],
    );
    const carrierSet = Boolean(carrierName.trim() || carrierNaic.trim());

    const handleSave = async () => {
        setSaveStatus('saving');
        setSaveMessage(null);
        const res = await saveClientFilingDefaults(token, {
            partnersId,
            carrier: carrierSet ? { name: carrierName.trim() || null, naic: carrierNaic.trim() || null } : null,
            states: rows.map((r) => ({
                stateId: r.stateId,
                coverageOverride: r.coverageOverride || null,
                // Only special-license states carry a license override.
                licenseOverride: r.special ? (r.licenseOverride || null) : undefined,
            })),
        });
        if (res.ok) {
            setSaveStatus('saved');
            setBaseline(snapshot(carrierName, carrierNaic, rows));
        } else {
            setSaveStatus('error');
            setSaveMessage(res.unavailable
                ? 'Saving is not enabled yet — the defaults service is still being deployed. Your entries are not lost; try again once it is live.'
                : (res.message || 'Save failed — try again.'));
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6 }}>
                <CircularProgress size={22} sx={{ color: BLUE }} />
                <Typography sx={{ color: SLATE_400, fontSize: '14px' }}>Loading client defaults…</Typography>
            </Box>
        );
    }

    const pad = dense ? 1.5 : 2.5;

    return (
        <Box>
            {backendUnavailable && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '13px', borderRadius: '10px' }}>
                    The defaults service is still being deployed. You can set values now to preview the layout,
                    but <strong>saving is not enabled until the backend is live</strong>.
                </Alert>
            )}

            {/* ── Default carrier (client-wide) ── hidden in coverage-only mode (Partners page section). */}
            {!coverageOnly && (
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', p: pad, mb: 2.5, backgroundColor: '#fff' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#0f172a', mb: 0.5 }}>
                    Default carrier{partnerName ? ` — ${partnerName}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: SLATE_400, mb: 2 }}>
                    Applied to every policy for this client, bypassing extraction. Leave blank to keep using the
                    extracted carrier.
                </Typography>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Carrier" value={carrierName}
                            onChange={(e) => setCarrierName(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth size="small" label="NAIC" value={carrierNaic}
                            onChange={(e) => setCarrierNaic(e.target.value)}
                            helperText={carrierNaicDefault ? `System resolves: ${carrierNaicDefault}` : 'Resolved from the carrier'}
                            FormHelperTextProps={{ sx: { fontSize: '11px', color: SLATE_400 } }} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Box
                            component="button"
                            type="button"
                            disabled={!carrierSet}
                            onClick={() => { setCarrierName(''); setCarrierNaic(''); }}
                            sx={{ ...REVERT_SX, mt: 1 }}
                        >
                            Revert to default
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            )}

            {/* ── Per-state coverage + (special-license) license ── */}
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', p: pad, mb: 2.5, backgroundColor: '#fff' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#0f172a', mb: 0.5 }}>
                    {coverageOnly ? 'Default coverage code by state' : 'Coverage code & license by state'}
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: SLATE_400, mb: 2 }}>
                    {coverageOnly
                        ? 'Set the default coverage code per state for this client — it overrides the extracted code at filing time. Blank falls back to the default shown.'
                        : 'Set the default coverage code per state. FL, CA, WA and NJ also need an agency / state license; every other state files on the NPN from the License Matrix. Blank falls back to the default shown.'}
                </Typography>

                {rows.length === 0 ? (
                    <Typography sx={{ fontSize: '13px', color: SLATE_400 }}>No filing-enabled states.</Typography>
                ) : rows.map((r) => (
                    <Box key={r.stateId} sx={{ py: 1.75, borderTop: `1px solid ${BORDER}`, '&:first-of-type': { borderTop: 'none', pt: 0 } }}>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={3}>
                                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>{r.stateName}</Typography>
                                <Typography sx={{ fontSize: '11.5px', color: SLATE_400 }}>{r.postalAbbv}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={(!coverageOnly && r.special) ? 5 : 9}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: SLATE_500, mb: 0.5 }}>Coverage code</Typography>
                                <CoverageCodePicker
                                    token={token}
                                    state={r.postalAbbv}
                                    value={r.coverageOverride ?? ''}
                                    onChange={(code) => setRowField(r.stateId, 'coverageOverride', code)}
                                    label="Default coverage code"
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                                    <Typography sx={{ fontSize: '11.5px', color: SLATE_400 }}>
                                        {r.coverageDefault
                                            ? `Default: ${r.coverageDefault}${r.coverageDefaultName ? ` — ${r.coverageDefaultName}` : ''}`
                                            : 'Default: from extraction'}
                                    </Typography>
                                    <Box component="button" type="button" disabled={!r.coverageOverride}
                                        onClick={() => setRowField(r.stateId, 'coverageOverride', null)} sx={REVERT_SX}>
                                        Revert
                                    </Box>
                                </Box>
                            </Grid>

                            {!coverageOnly && r.special && (
                                <Grid item xs={12} sm={4}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: SLATE_500, mb: 0.5 }}>Agency / state license</Typography>
                                    <TextField fullWidth size="small" label="License number"
                                        value={r.licenseOverride ?? ''}
                                        onChange={(e) => setRowField(r.stateId, 'licenseOverride', e.target.value)} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                                        <Typography sx={{ fontSize: '11.5px', color: SLATE_400 }}>
                                            {r.licenseDefault ? `Matrix: ${r.licenseDefault}` : 'Matrix: none on file'}
                                        </Typography>
                                        <Box component="button" type="button" disabled={!r.licenseOverride}
                                            onClick={() => setRowField(r.stateId, 'licenseOverride', null)} sx={REVERT_SX}>
                                            Revert
                                        </Box>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                ))}
            </Box>

            {/* ── Save bar ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                {saveStatus === 'saved' && <Typography sx={{ fontSize: '13px', color: '#1e7e34', fontWeight: 700 }}>Saved ✓</Typography>}
                {saveStatus === 'error' && saveMessage && <Typography sx={{ fontSize: '12.5px', color: '#c62828', fontWeight: 600, maxWidth: 460, textAlign: 'right' }}>{saveMessage}</Typography>}
                <Button variant="contained" onClick={handleSave} disabled={saveStatus === 'saving' || !hasChanges}
                    sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: BLUE, '&:hover': { backgroundColor: BLUE_HOVER } }}>
                    {saveStatus === 'saving' ? 'Saving…' : 'Save defaults'}
                </Button>
            </Box>
        </Box>
    );
}

export default ClientFilingDefaultsPanel;
